import { useMemo, useState } from "react";
import { Picker } from "@design-system/Picker/Picker.lib";
import { VariantGrid, VariantItem } from "./playgroundControls";
import styles from "./PickerPlayground.module.scss";

interface AccountRow {
    id: string;
    name: string;
    balance: string;
}

const accountRows: AccountRow[] = [
    { id: "acc-operating", name: "Operating account", balance: "$48,200" },
    { id: "acc-payroll", name: "Payroll account", balance: "$12,900" },
    { id: "acc-card", name: "Corporate card", balance: "$3,400" },
];

/**
 * Worked example of the id-mapping recipe from the migration plan: the
 * caller keeps its object rows, the picker holds only ids, and the object
 * is looked up again on change and for `renderValue`.
 */
export const PickerObjectRowsGrid = () => {
    const [selected, setSelected] = useState<AccountRow | null>(accountRows[0]);
    const [selectedMany, setSelectedMany] = useState<AccountRow[]>([]);
    const rowsById = useMemo(
        () => new Map(accountRows.map((row) => [row.id, row])),
        [],
    );
    const items = useMemo(
        () =>
            accountRows.map((row) => ({
                value: row.id,
                label: row.name,
                rightText: row.balance,
            })),
        [],
    );

    return (
        <VariantGrid title="Object rows mapped to ids" columns="wide">
            <VariantItem label="single · value is the object, picker holds its id">
                <div className={styles.matrixCell}>
                    <Picker.Select
                        items={items}
                        value={selected?.id ?? null}
                        onValueChange={(id) =>
                            setSelected(
                                id === null ? null : (rowsById.get(id) ?? null),
                            )
                        }
                        renderValue={([id]) => {
                            const row = rowsById.get(id);
                            return row === undefined ? null : (
                                <span>
                                    {row.name} <small>{row.balance}</small>
                                </span>
                            );
                        }}
                    />
                </div>
            </VariantItem>
            <VariantItem label="multiple · ids back to objects, misses dropped">
                <div className={styles.matrixCell}>
                    <Picker.Select
                        items={items}
                        multiple
                        trigger="input"
                        value={selectedMany.map((row) => row.id)}
                        onValueChange={(ids) =>
                            setSelectedMany(
                                ids.flatMap((id) => {
                                    const row = rowsById.get(id);
                                    return row === undefined ? [] : [row];
                                }),
                            )
                        }
                        placeholder="Pick accounts"
                    />
                </div>
            </VariantItem>
        </VariantGrid>
    );
};

const largeItems = Array.from({ length: 300 }, (_, index) => ({
    value: `row-${index}`,
    label: `Vendor ${String(index + 1).padStart(3, "0")}`,
    rightText: `$${((index * 37) % 900) + 100}`,
}));

/** 300 non-filtering rows: a keystroke here must not re-render the rows (render stability check). */
export const PickerLargeListGrid = () => (
    <VariantGrid title="Large list (300 rows)" columns="wide">
        <VariantItem label='trigger="input" · filterable={false} · 300 rows'>
            <div className={styles.matrixCell}>
                <Picker.Select
                    items={largeItems}
                    trigger="input"
                    filterable={false}
                    placeholder="Type to test keystroke cost"
                />
            </div>
        </VariantItem>
    </VariantGrid>
);
